import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Music, Music2 } from "lucide-react";

const MusicPlayer = forwardRef(({ autoPlay = false }, ref) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const audioRef = useRef(null);

  // Chinese Bamboo Flute & Guzheng Instrumental (royalty-free from Internet Archive)
  const musicUrl = "./sound.mp3";

  useEffect(() => {
    audioRef.current = new Audio(musicUrl);
    audioRef.current.loop = true;
    audioRef.current.volume = 0.25;

    audioRef.current.addEventListener("canplaythrough", () => {
      setIsLoaded(true);
    });

    // Try to auto-play on first load
    const attemptAutoPlay = () => {
      if (audioRef.current) {
        audioRef.current
          .play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch(() => {
            // Autoplay was blocked, user will need to click
            setIsPlaying(false);
          });
      }
    };

    // Attempt autoplay after a short delay
    const timer = setTimeout(attemptAutoPlay, 500);

    return () => {
      clearTimeout(timer);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (autoPlay && audioRef.current && isLoaded) {
      audioRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    }
  }, [autoPlay, isLoaded]);

  useImperativeHandle(ref, () => ({
    play: () => {
      if (audioRef.current) {
        audioRef.current
          .play()
          .then(() => setIsPlaying(true))
          .catch(() => setIsPlaying(false));
      }
    },
    pause: () => {
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    },
    toggle: () => {
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          audioRef.current
            .play()
            .then(() => setIsPlaying(true))
            .catch(() => setIsPlaying(false));
        }
      }
    },
  }));

  const toggleMusic = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current
          .play()
          .then(() => setIsPlaying(true))
          .catch((error) => {
            console.log("Playback failed:", error);
            setIsPlaying(false);
          });
      }
    }
  };

  return (
    <button
      onClick={toggleMusic}
      className="music-toggle"
      data-testid="music-toggle-btn"
      aria-label={isPlaying ? "Pause music" : "Play music"}
    >
      <div
        className={`transition-transform duration-300 ${
          isPlaying ? "vinyl-spinning" : ""
        }`}
      >
        {isPlaying ? (
          <Music2 className="w-6 h-6 md:w-7 md:h-7 text-[#D4AF37]" />
        ) : (
          <Music className="w-6 h-6 md:w-7 md:h-7 text-[#8B0000]" />
        )}
      </div>
      {isPlaying && (
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#8B0000] rounded-full animate-pulse" />
      )}
    </button>
  );
});

MusicPlayer.displayName = "MusicPlayer";

export default MusicPlayer;
